import {ApolloLink, type FetchResult, Observable} from '@apollo/client';

import {recordRumDurationVital} from './datadog/datadogRum';

export const LATEST_WINS_REQUEST_KEY = 'latestWinsRequestKey';

type ActiveRequest = {
  cancel: () => void;
};

export function latestWinsContext(requestKey: object): Record<string, unknown> {
  return {
    [LATEST_WINS_REQUEST_KEY]: requestKey,
    // Apollo deduplication can share one fetch between hook instances. A
    // lane-owned AbortController must never cancel another hook's live request.
    queryDeduplication: false,
  };
}

export function createLatestWinsLink(): ApolloLink {
  const activeRequests = new WeakMap<object, ActiveRequest>();

  return new ApolloLink((operation, forward) => {
    const requestKey = operation.getContext()[LATEST_WINS_REQUEST_KEY];
    if (
      requestKey == null ||
      (typeof requestKey !== 'object' && typeof requestKey !== 'function')
    ) {
      return forward(operation);
    }

    return new Observable<FetchResult>(observer => {
      const previousRequest = activeRequests.get(requestKey);
      previousRequest?.cancel();

      const controller = new AbortController();
      const activeRequest: ActiveRequest = {
        cancel: () => {
          const duration = performance.now() - dispatchedAt;
          operation.setContext({doNotRetry: true});
          controller.abort();
          // A pending RetryLink timer does not observe the abort signal.
          // Completion runs our teardown, unsubscribing and clearing that timer.
          observer.complete();
          recordRumDurationVital('workspace.history_keys_request_cancelled', {
            startTime: performance.timeOrigin + dispatchedAt,
            duration,
            context: {
              'workspace.operation_name': operation.operationName,
              'workspace.request_outcome': 'cancelled',
              'workspace.cancellation_reason': 'superseded',
            },
          });
        },
      };
      activeRequests.set(requestKey, activeRequest);
      operation.setContext(({fetchOptions = {}}) => ({
        fetchOptions: {...fetchOptions, signal: controller.signal},
      }));

      let settled = false;
      const finish = () => {
        settled = true;
        if (activeRequests.get(requestKey) === activeRequest) {
          activeRequests.delete(requestKey);
        }
      };
      // Keep the original dispatch timestamp across downstream retries/backoff.
      const dispatchedAt = performance.now();
      const subscription = forward(operation).subscribe({
        next: result => observer.next(result),
        error: error => {
          finish();
          if (controller.signal.aborted) {
            // Cancellation is expected control flow. The operation is marked
            // doNotRetry before abort, and completion prevents error and
            // service-health links outside this link from reporting it.
            observer.complete();
          } else {
            observer.error(error);
          }
        },
        complete: () => {
          finish();
          observer.complete();
        },
      });

      return () => {
        if (!settled) {
          operation.setContext({doNotRetry: true});
          controller.abort();
          finish();
        }
        subscription.unsubscribe();
      };
    });
  });
}
